import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, Phone, Mail, MapPin, TrendingUp, AlertTriangle, 
  CheckCircle, Clock, Calendar, Plus, ChevronRight, Target,
  Building, FileText, MessageSquare, ClipboardCheck, BarChart3,
  Star, Filter, Search, ArrowRight, Flame
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lead stage colors
const STAGE_COLORS = {
  new_enquiry: 'bg-blue-100 text-blue-800',
  contacted: 'bg-indigo-100 text-indigo-800',
  interested: 'bg-purple-100 text-purple-800',
  meeting_scheduled: 'bg-yellow-100 text-yellow-800',
  site_visit_done: 'bg-orange-100 text-orange-800',
  financial_review: 'bg-pink-100 text-pink-800',
  under_discussion: 'bg-cyan-100 text-cyan-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  on_hold: 'bg-gray-100 text-gray-800'
};

const PRIORITY_COLORS = {
  high: 'bg-red-100 text-red-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-green-100 text-green-800'
};

export default function FranchiseDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/franchise/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      } else {
        toast.error('Failed to load dashboard');
      }
    } catch (err) {
      toast.error('Error loading dashboard');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center text-gray-500">
            Failed to load dashboard data
          </CardContent>
        </Card>
      </div>
    );
  }

  const { stats, pipeline, todays_actions, recent_leads, recent_issues } = dashboard;

  return (
    <div className="p-6 space-y-6" data-testid="franchise-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Franchise Management</h1>
          <p className="text-slate-500">Manage leads, stores, and franchise operations</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDashboard}>
            Refresh
          </Button>
          <Button 
            className="bg-red-600 hover:bg-red-700"
            onClick={() => navigate('/franchise/leads/new')}
            data-testid="header-new-lead-btn"
          >
            <Plus size={16} className="mr-2" />
            New Lead
          </Button>
        </div>
      </div>

      {/* Top Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard
          title="Total Leads"
          value={stats.total_leads}
          icon={Users}
          color="blue"
          subtitle={`+${stats.new_leads_this_week} this week`}
        />
        <StatCard
          title="Hot Leads"
          value={stats.hot_leads}
          icon={Flame}
          color="red"
          subtitle="High priority"
        />
        <StatCard
          title="Meetings"
          value={stats.meetings_scheduled}
          icon={Calendar}
          color="yellow"
          subtitle="Scheduled"
        />
        <StatCard
          title="In Progress"
          value={stats.deals_in_progress}
          icon={TrendingUp}
          color="purple"
          subtitle="Active deals"
        />
        <StatCard
          title="Live Stores"
          value={stats.stores_live}
          icon={Building}
          color="green"
          subtitle={`+${stats.stores_opening_soon} opening`}
        />
        <StatCard
          title="Open Issues"
          value={stats.open_issues}
          icon={AlertTriangle}
          color="orange"
          subtitle="Need attention"
        />
      </div>

      {/* Alerts Section */}
      {(stats.overdue_followups > 0 || stats.overdue_tasks > 0) && (
        <Card className="border-l-4 border-l-red-500 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <AlertTriangle className="text-red-500" size={24} />
              <div className="flex-1">
                <h3 className="font-semibold text-red-800">Action Required</h3>
                <div className="flex gap-4 mt-1 text-sm text-red-700">
                  {stats.overdue_followups > 0 && (
                    <span>{stats.overdue_followups} overdue follow-ups</span>
                  )}
                  {stats.overdue_tasks > 0 && (
                    <span>{stats.overdue_tasks} overdue tasks</span>
                  )}
                  {stats.pending_documents > 0 && (
                    <span>{stats.pending_documents} pending documents</span>
                  )}
                </div>
              </div>
              <Button variant="destructive" size="sm" onClick={() => setActiveTab('leads')}>
                View Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-flex">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="leads">Leads Pipeline</TabsTrigger>
          <TabsTrigger value="today">Today's Actions</TabsTrigger>
          <TabsTrigger value="issues">Support Issues</TabsTrigger>
          <TabsTrigger value="kpi">KPI</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pipeline Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target size={20} />
                  Lead Pipeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(pipeline).map(([stage, count]) => (
                    <div key={stage} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={STAGE_COLORS[stage]}>{formatStage(stage)}</Badge>
                      </div>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
                <Button 
                  variant="outline" 
                  className="w-full mt-4"
                  onClick={() => setActiveTab('leads')}
                  data-testid="view-all-leads-btn"
                >
                  View All Leads
                  <ChevronRight size={16} className="ml-2" />
                </Button>
              </CardContent>
            </Card>

            {/* Recent Leads */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users size={20} />
                  Recent Leads
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recent_leads.map((lead) => (
                    <div 
                      key={lead.id} 
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/franchise/leads/${lead.id}`)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-semibold">
                          {lead.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{lead.name}</p>
                          <p className="text-xs text-gray-500">{lead.preferred_location || 'No location'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {lead.is_hot_lead && <Flame size={14} className="text-red-500" />}
                        <Badge className={STAGE_COLORS[lead.stage] + ' text-xs'}>
                          {formatStage(lead.stage)}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <QuickLinkCard
              title="Leads"
              description="Manage pipeline"
              icon={Users}
              onClick={() => setActiveTab('leads')}
              color="blue"
              testId="quicklink-leads"
            />
            <QuickLinkCard
              title="Onboarding"
              description="Track progress"
              icon={ClipboardCheck}
              onClick={() => navigate('/franchise/onboarding')}
              color="green"
              testId="quicklink-onboarding"
            />
            <QuickLinkCard
              title="Stores"
              description="View all stores"
              icon={Building}
              onClick={() => navigate('/franchise/stores')}
              color="purple"
              testId="quicklink-stores"
            />
            <QuickLinkCard
              title="Support"
              description="Handle issues"
              icon={MessageSquare}
              onClick={() => navigate('/franchise/issues')}
              color="orange"
              testId="quicklink-support"
            />
          </div>
        </TabsContent>

        {/* Today's Actions Tab */}
        <TabsContent value="today" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Today's Follow-ups */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Phone size={20} />
                  Follow-ups Due Today
                </CardTitle>
              </CardHeader>
              <CardContent>
                {todays_actions.followups.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No follow-ups scheduled for today</p>
                ) : (
                  <div className="space-y-3">
                    {todays_actions.followups.map((lead) => (
                      <div 
                        key={lead.id} 
                        className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
                        onClick={() => navigate(`/franchise/leads/${lead.id}`)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{lead.name}</p>
                            {lead.business_name && (
                              <p className="text-sm text-gray-500">{lead.business_name}</p>
                            )}
                          </div>
                          <Badge className={STAGE_COLORS[lead.stage]}>
                            {formatStage(lead.stage)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Phone size={14} />
                            {lead.phone}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Today's Meetings */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar size={20} />
                  Meetings & Site Visits
                </CardTitle>
              </CardHeader>
              <CardContent>
                {todays_actions.meetings.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No meetings scheduled for today</p>
                ) : (
                  <div className="space-y-3">
                    {todays_actions.meetings.map((meeting) => (
                      <div key={meeting.id} className="p-3 border rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{meeting.summary}</p>
                            <p className="text-sm text-gray-500">{meeting.comm_type}</p>
                          </div>
                          <Badge variant="outline">{meeting.scheduled_time || 'TBD'}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Leads Pipeline Tab */}
        <TabsContent value="leads">
          <LeadsPipelineView token={token} />
        </TabsContent>

        {/* Support Issues Tab */}
        <TabsContent value="issues">
          <SupportIssuesView token={token} recentIssues={recent_issues} />
        </TabsContent>

        {/* KPI Tab */}
        <TabsContent value="kpi">
          <KPIDashboardView token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Helper Components
function StatCard({ title, value, icon: Icon, color, subtitle }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    red: 'bg-red-50 text-red-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    purple: 'bg-purple-50 text-purple-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600'
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon size={20} />
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-gray-500">{title}</p>
            {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickLinkCard({ title, description, icon: Icon, href, onClick, color, testId }) {
  const colorClasses = {
    blue: 'bg-blue-50 hover:bg-blue-100 text-blue-600',
    green: 'bg-green-50 hover:bg-green-100 text-green-600',
    purple: 'bg-purple-50 hover:bg-purple-100 text-purple-600',
    orange: 'bg-orange-50 hover:bg-orange-100 text-orange-600'
  };

  const handle = () => {
    if (onClick) {
      onClick();
      return;
    }
    if (href) {
      window.location.href = href;
    }
  };

  return (
    <Card
      className={`cursor-pointer transition-colors ${colorClasses[color]}`}
      onClick={handle}
      data-testid={testId}
    >
      <CardContent className="p-4 flex items-center gap-3">
        <Icon size={24} />
        <div>
          <p className="font-semibold">{title}</p>
          <p className="text-xs opacity-75">{description}</p>
        </div>
        <ArrowRight size={16} className="ml-auto" />
      </CardContent>
    </Card>
  );
}

function formatStage(stage) {
  return stage?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// Leads Pipeline View Component
function LeadsPipelineView({ token }) {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        let url = `${API_URL}/api/franchise/leads?limit=100`;
        if (filter !== 'all') url += `&stage=${filter}`;
        if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
        
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setLeads(data.leads);
        }
      } catch (err) {
        console.error('Error fetching leads:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLeads();
  }, [token, filter, searchQuery]);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search leads..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          className="px-4 py-2 border rounded-lg"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="all">All Stages</option>
          <option value="new_enquiry">New Enquiry</option>
          <option value="contacted">Contacted</option>
          <option value="interested">Interested</option>
          <option value="meeting_scheduled">Meeting Scheduled</option>
          <option value="site_visit_done">Site Visit Done</option>
          <option value="financial_review">Financial Review</option>
          <option value="under_discussion">Under Discussion</option>
          <option value="approved">Approved</option>
        </select>
        <Button 
          className="bg-red-600 hover:bg-red-700"
          onClick={() => navigate('/franchise/leads/new')}
          data-testid="pipeline-new-lead-btn"
        >
          <Plus size={16} className="mr-2" />
          New Lead
        </Button>
      </div>

      {/* Leads Table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
        </div>
      ) : leads.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            No leads found. Create your first lead to get started.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-600">Lead</th>
                    <th className="text-left p-4 font-medium text-slate-600">Contact</th>
                    <th className="text-left p-4 font-medium text-slate-600">Location</th>
                    <th className="text-left p-4 font-medium text-slate-600">Stage</th>
                    <th className="text-left p-4 font-medium text-slate-600">
                      <span
                        className="inline-flex items-center gap-1 cursor-help"
                        title={
                          'Auto-calculated 0–100 priority score.\n' +
                          '• 50 base\n' +
                          '• +10 budget given · +5 location · +10 business name · +5 email\n' +
                          '• +stage progress (New 0 · Contacted 5 · Interested 15 · Meeting 25 · Site Visit 35 · Financial 45 · Discussion 55 · Approved 100)\n' +
                          '• +20 if flagged Hot Lead\n' +
                          'Capped at 100. 80+ = high intent, 60–79 warm, <60 needs nurturing.'
                        }
                      >
                        Score <span className="text-slate-400">ⓘ</span>
                      </span>
                    </th>
                    <th className="text-left p-4 font-medium text-slate-600">Next Follow-up</th>
                    <th className="text-left p-4 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id} className="border-b hover:bg-slate-50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-semibold text-sm">
                            {lead.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium flex items-center gap-1">
                              {lead.name}
                              {lead.is_hot_lead && <Flame size={14} className="text-red-500" />}
                            </p>
                            {lead.business_name && (
                              <p className="text-sm text-gray-500">{lead.business_name}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm">
                          <p className="flex items-center gap-1">
                            <Phone size={12} />
                            {lead.phone}
                          </p>
                          {lead.email && (
                            <p className="flex items-center gap-1 text-gray-500">
                              <Mail size={12} />
                              {lead.email}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="flex items-center gap-1 text-sm">
                          <MapPin size={12} />
                          {lead.preferred_location || '-'}
                        </span>
                      </td>
                      <td className="p-4">
                        <Badge className={STAGE_COLORS[lead.stage]}>
                          {formatStage(lead.stage)}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div className="w-12 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-red-500 rounded-full"
                              style={{ width: `${lead.lead_score}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium">{lead.lead_score}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        {lead.next_followup_date ? (
                          <span className={`text-sm ${
                            new Date(lead.next_followup_date) < new Date() 
                              ? 'text-red-600 font-medium' 
                              : 'text-gray-600'
                          }`}>
                            {new Date(lead.next_followup_date).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate(`/franchise/leads/${lead.id}`)}
                          data-testid={`view-lead-${lead.id}`}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Support Issues View Component
function SupportIssuesView({ token, recentIssues }) {
  const [issues, setIssues] = useState(recentIssues || []);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('open');

  useEffect(() => {
    const fetchIssues = async () => {
      setLoading(true);
      try {
        let url = `${API_URL}/api/franchise/issues`;
        if (statusFilter !== 'all') {
          const statusParam = statusFilter === 'open' ? 'new' : statusFilter;
          url += `?status=${statusParam}`;
        }
        
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setIssues(data.issues);
        }
      } catch (err) {
        console.error('Error fetching issues:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchIssues();
  }, [token, statusFilter]);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4 items-center">
        <select
          className="px-4 py-2 border rounded-lg"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="open">Open Issues</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="all">All Issues</option>
        </select>
        <Button 
          className="bg-red-600 hover:bg-red-700"
          onClick={() => window.location.href = '/franchise/issues/new'}
        >
          <Plus size={16} className="mr-2" />
          Report Issue
        </Button>
      </div>

      {/* Issues List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
        </div>
      ) : issues.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            No issues found.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {issues.map((issue) => (
            <Card key={issue.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={PRIORITY_COLORS[issue.priority]}>
                        {issue.priority}
                      </Badge>
                      <Badge variant="outline">{issue.category}</Badge>
                      <Badge variant={issue.status === 'resolved' ? 'default' : 'secondary'}>
                        {issue.status}
                      </Badge>
                    </div>
                    <p className="font-medium">{issue.store_name}</p>
                    <p className="text-sm text-gray-600 mt-1">{issue.description}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      Reported by {issue.raised_by_name} on {new Date(issue.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.location.href = `/franchise/issues/${issue.id}`}
                  >
                    View Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// KPI Dashboard View Component
function KPIDashboardView({ token }) {
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchKPI = async () => {
      try {
        const res = await fetch(`${API_URL}/api/franchise/kpi`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setKpi(data);
        }
      } catch (err) {
        console.error('Error fetching KPI:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchKPI();
  }, [token]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!kpi) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-gray-500">
          Failed to load KPI data
        </CardContent>
      </Card>
    );
  }

  const { scores, overall_score, stats } = kpi;

  return (
    <div className="space-y-6">
      {/* Overall Score */}
      <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg opacity-90">Overall Performance Score</p>
              <p className="text-5xl font-bold mt-2">{overall_score}%</p>
            </div>
            <div className="text-6xl opacity-50">
              <Star />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Scores Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <KPIScoreCard title="Lead Response" score={scores.lead_response} />
        <KPIScoreCard title="Follow-up Completion" score={scores.followup_completion} />
        <KPIScoreCard title="Lead Conversion" score={scores.lead_conversion} />
        <KPIScoreCard title="Meetings Completed" score={scores.meetings_completed} />
        <KPIScoreCard title="Approvals Progressed" score={scores.approvals_progressed} />
        <KPIScoreCard title="Onboarding Completion" score={scores.onboarding_completion} />
        <KPIScoreCard title="Support Response" score={scores.support_response} />
        <KPIScoreCard title="Issue Resolution" score={scores.issue_resolution} />
        <KPIScoreCard title="Inspection Follow-up" score={scores.inspection_followup} />
        <KPIScoreCard title="Communication Logging" score={scores.communication_logging} />
        <KPIScoreCard title="Engagement" score={scores.engagement} />
      </div>

      {/* Stats Summary */}
      <Card>
        <CardHeader>
          <CardTitle>30-Day Performance Stats</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-red-600">{stats.total_leads_30d}</p>
              <p className="text-sm text-gray-500">New Leads</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{stats.followups_completed}</p>
              <p className="text-sm text-gray-500">Follow-ups Done</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{stats.conversion_rate}%</p>
              <p className="text-sm text-gray-500">Conversion Rate</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{stats.communications_logged}</p>
              <p className="text-sm text-gray-500">Comms Logged</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-orange-600">{stats.issues_resolved}</p>
              <p className="text-sm text-gray-500">Issues Resolved</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function KPIScoreCard({ title, score }) {
  const getColor = (s) => {
    if (s >= 80) return 'text-green-600 bg-green-50';
    if (s >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <Card>
      <CardContent className="p-4 text-center">
        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${getColor(score)} font-bold text-lg mb-2`}>
          {score}
        </div>
        <p className="text-sm text-gray-600">{title}</p>
      </CardContent>
    </Card>
  );
}
